import { Injectable } from '@angular/core';
import { DroppedWidgetIntoLayoutWidgetAction } from '@app/finxone-web-frontend/app/lib/actions/dropped.action';
import {
  ComponentLabel,
  GridPosition,
  NewUiZoneWidgetConfig,
  RightWidgetConfiguration,
  WORKFLOW_POPUPS,
  WidgetNames,
  WorkflowWidgetConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, SelectorOptions, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import {
  DeselectWidgets,
  DisplayPopupAction,
  DragStartAction,
  HighlightSelectedWidgets,
  NewWidgetEvent,
  SetCurrentCanvasComponent,
  SetFooterVisibleWidgetConfig,
  SetVisibleWidgetConfig,
} from '../actions/app-grid.action';

export interface AppGridStateModel {
  visibleWidgetConfig: [RightWidgetConfiguration, GridPosition | null][];
  footerVisibleWidgetConfig: [RightWidgetConfiguration, GridPosition | null][];
  setVisibleCanvasConfig: {
    component: ComponentLabel;
    zone?: string;
    pageIndexOrSectionName?: number | string;
  };
  currentNewWidgetDetails: {
    widgetName: string;
    uniqueId: string | number;
  };
  dragState: {
    draggedWidgetConfig: WorkflowWidgetConfig | null;
  };
  popupsVisibilityConfig: {
    isVisible: WORKFLOW_POPUPS | null;
  };
}

@SelectorOptions({ injectContainerState: false })
@State<AppGridStateModel>({
  name: 'appGridState',
  defaults: {
    visibleWidgetConfig: [],
    footerVisibleWidgetConfig: [],
    setVisibleCanvasConfig: {
      component: WidgetNames.ACCOUNT_LIST_WIDGET,
    },
    currentNewWidgetDetails: {
      widgetName: '',
      uniqueId: '',
    },
    dragState: {
      draggedWidgetConfig: null,
    },
    popupsVisibilityConfig: {
      isVisible: null,
    },
  },
  children: [],
})
@Injectable()
export class AppGridState {
  private highlightedWidgets: string[] = [];
  @Selector()
  static getVisibleWidgetConfig(state: AppGridStateModel): [RightWidgetConfiguration, GridPosition | null][] {
    return state?.visibleWidgetConfig;
  }

  @Selector()
  static getFooterVisibleWidgetConfig(
    state: AppGridStateModel,
  ): [RightWidgetConfiguration, GridPosition | null][] {
    return state.footerVisibleWidgetConfig;
  }

  /*
    Used with { injectContainerState: false } on selector options to ensure
    selector getVisibleCanvasComponent only triggers on changes to visibleCanvasComponent
  */
  @Selector([AppGridState])
  private static internalVisibleCanvasComponent(state: AppGridStateModel): {
    component: ComponentLabel;
    zone?: string;
    pageIndexOrSectionName?: number | string;
  } {
    return state.setVisibleCanvasConfig;
  }

  @Selector([AppGridState.internalVisibleCanvasComponent])
  static getVisibleCanvasComponent(config: {
    component: ComponentLabel;
    zone?: string;
    pageIndexOrSectionName?: number | string;
  }): {
    component: ComponentLabel;
    zone?: string;
    pageIndexOrSectionName?: number | string;
  } {
    return config;
  }

  /*
    Used with { injectContainerState: false } on selector options to ensure
    selector getVisibleCanvasComponent only triggers on changes to visibleCanvasComponent
  */
  @Selector([AppGridState])
  private static internalNewWidgetDetails(state: AppGridStateModel) {
    return state.currentNewWidgetDetails;
  }

  @Selector([AppGridState.internalNewWidgetDetails])
  static getNewWidgetDetails(currentNewWidgetDetails: { widgetName: string; uniqueId: string | number }): {
    widgetName: string;
    uniqueId: string | number;
  } {
    return currentNewWidgetDetails;
  }

  /*
    Used with { injectContainerState: false } on selector options to ensure
    selector getDraggedWidgetConfig only triggers on changes to draggedWidgetConfig
  */
  @Selector([AppGridState])
  private static internalDraggedWidgetConfig(state: AppGridStateModel) {
    return state.dragState.draggedWidgetConfig;
  }

  @Selector([AppGridState.internalDraggedWidgetConfig])
  static getDraggedWidgetConfig(
    draggedWidgetConfig: WorkflowWidgetConfig | null,
  ): WorkflowWidgetConfig | null {
    return draggedWidgetConfig;
  }

  @Action(SetVisibleWidgetConfig)
  setVisibleWidgetConfig(ctx: StateContext<AppGridStateModel>, action: SetVisibleWidgetConfig) {
    return ctx.patchState({
      visibleWidgetConfig: action.visibleWidgetConfig,
    });
  }

  @Action(SetFooterVisibleWidgetConfig)
  setFooterVisibleWidgetConfig(ctx: StateContext<AppGridStateModel>, action: SetFooterVisibleWidgetConfig) {
    return ctx.patchState({
      footerVisibleWidgetConfig: action.footerVisibleWidgetConfig,
    });
  }

  @Action(SetCurrentCanvasComponent)
  setCurrentCanvasComponent(ctx: StateContext<AppGridStateModel>, action: SetCurrentCanvasComponent) {
    return ctx.patchState({
      setVisibleCanvasConfig: {
        component: action.component,
        zone: action.zone,
        pageIndexOrSectionName: action.pageIndexOrSectionName,
      },
    });
  }

  @Action(NewWidgetEvent)
  setNewWidgetDetails(ctx: StateContext<AppGridStateModel>, action: NewWidgetEvent) {
    ctx.dispatch(new HighlightSelectedWidgets(action.id));
    return ctx.patchState({
      currentNewWidgetDetails: {
        widgetName: action.widgetName,
        uniqueId: action.id,
      },
    });
  }

  @Action(DragStartAction)
  dragStartAction(ctx: StateContext<AppGridStateModel>, action: DragStartAction) {
    return ctx.patchState({
      dragState: {
        draggedWidgetConfig: action.draggedWidgetConfig,
      },
    });
  }

  @Action(DisplayPopupAction)
  DisplayPopupAction(ctx: StateContext<AppGridStateModel>, action: DisplayPopupAction) {
    return ctx.patchState({
      popupsVisibilityConfig: {
        isVisible: action.popupType,
      },
    });
  }

  @Action(DroppedWidgetIntoLayoutWidgetAction)
  droppedWidgetIntoLayoutWidget(
    ctx: StateContext<AppGridStateModel>,
    action: DroppedWidgetIntoLayoutWidgetAction,
  ): Observable<NewUiZoneWidgetConfig> {
    return of(action.widgetDroppedInto);
  }

  /**
   * Method to toggle highlighting of selected and previously selected widget(s)
   * @param action - widgetUniqueId: string, multipleSelection: boolean
   */
  @Action(HighlightSelectedWidgets)
  highlightSelectedWidgets(ctx: StateContext<AppGridStateModel>, action: HighlightSelectedWidgets) {
    if (action?.multipleSelection) {
      if (this.highlightedWidgets.includes(action.widgetUniqueId.toString())) {
        this.removeOutlineFromElement(action.widgetUniqueId?.toString());
        this.highlightedWidgets = this.highlightedWidgets.filter(
          (widget) => widget !== action.widgetUniqueId?.toString(),
        );
      } else {
        this.highlightedWidgets.push(action.widgetUniqueId?.toString());
        this.applyOutlineToElement(action.widgetUniqueId?.toString());
      }
    } else {
      for (const widget of this.highlightedWidgets) {
        this.removeOutlineFromElement(widget);
      }
      this.highlightedWidgets = [action.widgetUniqueId?.toString()];
      this.applyOutlineToElement(action.widgetUniqueId?.toString());
    }
  }

  @Action(DeselectWidgets)
  deselectWidgets() {
    for (const widget of this.highlightedWidgets) {
      this.removeOutlineFromElement(widget);
    }
    this.highlightedWidgets = [];
  }

  private applyOutlineToElement(widgetUniqueId: string) {
    const widgetEl = document.getElementsByClassName(widgetUniqueId)[0] as HTMLElement;
    if (widgetEl) {
      widgetEl.style.outline = '3px solid var(--widget-outline)';
    }
  }

  private removeOutlineFromElement(widgetUniqueId: string) {
    const widgetEl = document.getElementsByClassName(widgetUniqueId)[0] as HTMLElement;
    if (widgetEl) {
      widgetEl.style.outline = 'unset';
    }
  }
}
